/**
 * nameComparison.ts - Comparación de Nombres entre Fuentes
 * 
 * Módulo extraído de crossValidation.ts (TD-002.G.4)
 * Implementa fuzzy matching para comparar nombres de infraestructuras
 * entre diferentes fuentes de geocodificación.
 * 
 * @created 2026-01-26
 */

import { calculateSimilarity } from '../fuzzySearch';
import { distanceUTM } from './geometry';
import { getSuccessfulResults } from './sources';
import type { SourceResult, UTMPoint as _UTMPoint } from './types';

// ============================================================================
// CONSTANTES
// ============================================================================

/** Umbral mínimo de similaridad para considerar un match (0-100) */
export const DEFAULT_NAME_SIMILARITY_THRESHOLD = 50;

/** Umbral por defecto para considerar puntos concordantes (metros) */
export const DEFAULT_CONCORDANCE_THRESHOLD_METERS = 100;

// ============================================================================
// COMPARACIÓN DE NOMBRES
// ============================================================================

/**
 * Compara nombres de infraestructuras entre fuentes usando fuzzy matching.
 * 
 * Usa el algoritmo de similaridad que combina Jaccard + uFuzzy para
 * detectar variaciones como:
 * - "Centro de Salud Quentar" vs "C.S. Quentar"
 * - "CEIP San José" vs "Colegio Público San Jose"
 * - "Ayto. Colomera" vs "Ayuntamiento de Colomera"
 * 
 * @param name1 - Nombre de primera fuente
 * @param name2 - Nombre de segunda fuente
 * @param threshold - Umbral mínimo de similaridad (default: 50)
 * @returns Objeto con match booleano y score numérico (0-100)
 * 
 * @example
 * const { match, score } = compareSourceNames(
 *   "Centro de Salud de Quentar",
 *   "C.S. Quentar"
 * );
 * // match: true, score: ~72
 */
export function compareSourceNames(
  name1: string | undefined,
  name2: string | undefined,
  threshold: number = DEFAULT_NAME_SIMILARITY_THRESHOLD
): { match: boolean; score: number } {
  if (!name1 || !name2) return { match: false, score: 0 };
  
  const similarity = calculateSimilarity(name1, name2);
  return {
    match: similarity >= threshold,
    score: similarity
  };
}

/**
 * Calcula score promedio de similaridad entre todos los nombres
 * de resultados exitosos de diferentes fuentes.
 * 
 * Compara cada par de nombres y calcula la media. Esto permite
 * detectar si las fuentes están hablando de la misma infraestructura
 * aunque la nombren diferente.
 * 
 * @param results - Array de resultados de fuentes
 * @returns Score promedio de similaridad (0-100), o 100 si no hay comparación posible
 * 
 * @example
 * // Con 3 fuentes que devolvieron nombres similares
 * const score = calculateCrossNameScore(results);
 * // score: ~85 (alta concordancia de nombres)
 */
export function calculateCrossNameScore(results: SourceResult[]): number {
  const successful = getSuccessfulResults(results);
  
  // Sin comparación posible: retornar 100 (no penalizar)
  if (successful.length < 2) return 100;
  
  let totalScore = 0;
  let comparisons = 0;
  
  // Comparar cada par de nombres
  for (let i = 0; i < successful.length; i++) {
    for (let j = i + 1; j < successful.length; j++) {
      const name1 = successful[i].result?.matchedName;
      const name2 = successful[j].result?.matchedName;
      
      if (name1 && name2) {
        totalScore += calculateSimilarity(name1, name2);
        comparisons++;
      }
    }
  }
  
  // Si no hubo comparaciones (sin nombres), retornar 100
  return comparisons > 0 ? totalScore / comparisons : 100;
}

/**
 * Determina si dos resultados de diferentes fuentes refieren a la misma
 * infraestructura, considerando tanto coordenadas como nombres.
 * 
 * Reglas de coincidencia:
 * 1. Coordenadas cercanas Y nombres similares → true
 * 2. Nombres muy similares (>80%) aunque coords difieran un poco más → true
 * 3. Coordenadas muy cercanas (<25m) aunque nombres no matcheen → true
 * 
 * @param r1 - Primer resultado
 * @param r2 - Segundo resultado
 * @param distanceThreshold - Umbral de distancia en metros (default: 100)
 * @param nameThreshold - Umbral de similaridad de nombre (default: 50)
 * @returns true si probablemente refieren a la misma infraestructura
 * 
 * @example
 * if (areSameInfrastructure(result1, result2)) {
 *   // Las fuentes están hablando del mismo lugar
 * }
 */
export function areSameInfrastructure(
  r1: SourceResult,
  r2: SourceResult,
  distanceThreshold: number = DEFAULT_CONCORDANCE_THRESHOLD_METERS,
  nameThreshold: number = DEFAULT_NAME_SIMILARITY_THRESHOLD
): boolean {
  if (!r1.result || !r2.result) return false;
  
  // Verificar cercanía de coordenadas
  const distance = distanceUTM(r1.result.coordinates, r2.result.coordinates);
  const coordsMatch = distance <= distanceThreshold;
  
  // Verificar similaridad de nombres
  const { match: namesMatch, score: nameScore } = compareSourceNames(
    r1.result.matchedName,
    r2.result.matchedName,
    nameThreshold
  );
  
  // Coinciden si:
  // 1. Coordenadas cercanas Y nombres similares
  // 2. Nombres muy similares (>80%) aunque coords difieran un poco más
  // 3. Coordenadas muy cercanas (<25m) aunque nombres no matcheen
  if (coordsMatch && namesMatch) return true;
  if (nameScore >= 80 && distance <= distanceThreshold * 1.5) return true;
  if (distance <= 25) return true;
  
  return false;
}
